import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Chip, Fade, Icon } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { CopyInput, PageHeader, RenderFields, SectionHeader } from '~/components';
import { LogoLoading } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { DocumentRepository, ReportingRepository, ReportingSettlementReport } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { EditToolbar, Inner } from '~/styles';
import { downloadFile, formatDateTime, formatString } from '~/util';
import { getFilenameForStatementReport } from '~/util/reporting';

interface Params {
  id: string;
}

enum DocumentType {
  Statement = 'statement',
  Report = 'report',
}

const getDocumentType = (rawType: string): DocumentType => {
  if (rawType === 'bank_account_daily_statement') {
    return DocumentType.Statement;
  }
  if (rawType === 'bank_account_monthly_statement') {
    return DocumentType.Statement;
  }

  return DocumentType.Report;
};

const Headers = {
  [DocumentType.Report]: {
    headline: 'Report',
  },
  [DocumentType.Statement]: {
    headline: 'Statement',
  },
};

const ButtonList = styled.div`
  display: flex;
  gap: 12px;
  flex-direction: column;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: row;
  }
`;

const DateRange = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Arrow = styled(Icon.ArrowRight)`
  display: inline-block;
  vertical-align: top;

  --icon-size: 16px;
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const StyledLoading = styled(LogoLoading)`
  top: 80px;
`;

interface DownloadButtonProps {
  filename: string;
  label: string;
  id?: string;
}

export const DownloadButton: React.FC<DownloadButtonProps> = (props) => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);

  const [running, setRunning] = useState<boolean>(false);

  const handleDownload = (docId?: string) => {
    if (!docId) {
      return;
    }

    setRunning(true);

    DocumentRepository.get(docId, { contentDisposition: `attachment; filename="${props.filename}"` })
      .then((response) => {
        downloadFile(response.url, props.filename);
      })
      .catch((error: any) => {
        addDangerNotification({
          content: error.message,
        });
      });
  };

  return (
    <Button
      onClick={() => handleDownload(props.id)}
      isDisabled={!props.id}
      size="small"
      icon={<Icon.AnimationDownload running={running} onDone={() => setRunning(false)} />}
    >
      {props.label}
    </Button>
  );
};

export const PageDocumentView: React.FC = () => {
  const addDangerNotification = useNotificationStore((s) => s.addDangerNotification);

  const navigate = useNavigate();
  const { id } = useParams<keyof Params>() as Params;
  const [report, setReport] = useState<ReportingSettlementReport>();

  const documentType = report?.type ? getDocumentType(report.type) : undefined;
  const formMethods = useForm<ReportingSettlementReport>();

  useEffect(() => {
    ReportingRepository.get(id)
      .then((response: any) => {
        setReport(response);
      })
      .catch((error: any) => {
        navigate(ROUTE.REPORTING);
        addDangerNotification({
          content: error.message,
        });
      });
  }, [id]);

  useEffect(() => {
    if (report) {
      formMethods.setValue('type', report.type || '-');
      formMethods.setValue('status', report.status || '-');
      formMethods.setValue('fromDate', report.fromDate || '-');
      formMethods.setValue('toDate', report.toDate || '-');
      formMethods.setValue('statementSubject', report.statementSubject || '');
      formMethods.setValue('initiatedAt', report.initiatedAt || '');
      formMethods.setValue('completedAt', report.completedAt || '');
    }
  }, [report]);

  return (
    <>
      <Fade show={!documentType} base={StyledLoading} />
      <Fade show={!!documentType}>
        {documentType && (
          <>
            <PageHeader text={Headers[documentType].headline} />

            <SectionHeader text="Download" />
            <Inner pt={0} px="var(--page-margin)">
              <ButtonList>
                {report?.pdfDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'pdf')}
                    label=".pdf File"
                    id={report?.pdfDocumentId}
                  />
                )}
                {report?.bai2DocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'bai2')}
                    label=".bai2 File"
                    id={report?.bai2DocumentId}
                  />
                )}
                {report?.csvDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'csv')}
                    label=".csv File"
                    id={report?.csvDocumentId}
                  />
                )}
                {report?.jsonDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'json')}
                    label=".json File"
                    id={report?.jsonDocumentId}
                  />
                )}
                {report?.parquetDocumentId && (
                  <DownloadButton
                    filename={getFilenameForStatementReport(report, 'parquet')}
                    label=".parquet File"
                    id={report?.parquetDocumentId}
                  />
                )}
              </ButtonList>
            </Inner>

            <FormProvider {...formMethods}>
              {RenderFields<ReportingSettlementReport>({
                sections: [
                  {
                    headline: 'Information',
                    fields: [
                      {
                        id: 'type',
                        label: 'Type',
                        children: ({ value }) => {
                          return <Chip size="large">{value ? formatString(String(value)) : '-'}</Chip>;
                        },
                      },
                      {
                        id: 'status',
                        label: 'Status',
                        children: ({ value }) => {
                          const status = String(value);
                          return value ? (
                            <Chip
                              size="large"
                              type={status === 'completed' ? 'success' : status === 'failed' ? 'danger' : 'default'}
                            >
                              {formatString(status)}
                            </Chip>
                          ) : (
                            <>-</>
                          );
                        },
                      },
                      {
                        id: 'fromDate',
                        label: 'Date',
                        children: ({ value }) => {
                          return (
                            <DateRange>
                              {value || '-'} <Arrow /> {report?.toDate || '-'}
                            </DateRange>
                          );
                        },
                      },
                      ...(report?.statementSubject
                        ? [
                            {
                              id: 'statementSubject' as keyof ReportingSettlementReport,
                              label: 'Statement subject',
                              children: ({ value }: { value: unknown }) => {
                                return <CopyInput value={String(value) || '-'} />;
                              },
                            },
                          ]
                        : []),
                      {
                        id: 'initiatedAt',
                        label: 'Initiated At',
                        children: ({ value }) => {
                          return (
                            <CopyInput value={value ? formatDateTime(value as any, { timeZoneFormat: 'utc' }) : '-'} />
                          );
                        },
                      },
                      {
                        id: 'completedAt',
                        label: 'Completed At',
                        children: ({ value }) => {
                          return (
                            <CopyInput value={value ? formatDateTime(value as any, { timeZoneFormat: 'utc' }) : '-'} />
                          );
                        },
                      },
                    ],
                  },
                ],
              })}

              <Inner>
                <EditToolbar>
                  <Button onClick={() => navigate(-1)} variant="secondary" size="small">
                    Back
                  </Button>
                </EditToolbar>
              </Inner>
            </FormProvider>
          </>
        )}
      </Fade>
    </>
  );
};
